import AppCard from '@nxsys/components/AppCard';
import AppIconCard from '@nxsys/components/AppIconCard';
import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import { useGetStatisticsClientApprovalReject } from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DraftPayrollModal, {
  DraftPayrollModalTypeEnum,
} from './components/DraftPayrollModal';

const ClientApprovalReject = () => {
  const { isBureauEmployer, isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  // Ireland's models depend on the product too: bureau has PAYE + RCT, umbrella
  // adds UMBRELLA. Hardcoding the bureau pair here filtered every Irish
  // umbrella payroll out of this surface's counts and lists.
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : isBureau
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : undefined;

  const { value, loading, run } = useGetStatisticsClientApprovalReject();
  const data = useMemo(() => value?.data, [value]);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState<DraftPayrollModalTypeEnum | null>(
    null,
  );

  useEffect(() => {
    run({ paymentModels: validPaymentModels });
  }, []);

   useEffect(() => {
      const handleReload = () => run({ paymentModels: validPaymentModels });
      window.addEventListener('dashboard:reload-clientApprovalReject', handleReload);
      return () =>
        window.removeEventListener('dashboard:reload-clientApprovalReject', handleReload);
    }, []);

  const handleOpenModal = (type: DraftPayrollModalTypeEnum) => {
    setModalType(type);
    setIsOpenModal(true);
  };

  const cbAfterAction = () => {
    run({ paymentModels: validPaymentModels });
    window.dispatchEvent(new CustomEvent('dashboard:reload-payments'));
    window.dispatchEvent(new CustomEvent('dashboard:reload-payrollImport'));
  };

  const items = [
    ...(!isBureauEmployer
      ? [
          {
            title: data?.totalRequestApproval,
            description: 'Request Approval',
            icon: <Icons.IconFileText />,
            onClick: () =>
              handleOpenModal(DraftPayrollModalTypeEnum.REQUEST_APPROVAL),
          },
        ]
      : []),
    ...(!isBureauEmployer ||
    (data?.totalAwaitingApproval && data?.totalAwaitingApproval > 0)
      ? [
          {
            title: data?.totalAwaitingApproval,
            description: 'Awaiting Approval',
            icon: <Icons.IconClock />,
            onClick: () =>
              handleOpenModal(DraftPayrollModalTypeEnum.AWAITING_APPROVAL),
          },
        ]
      : []),
    ...(!isBureauEmployer
      ? [
          {
            title: data?.totalClientRejection,
            description: 'Client Rejection',
            icon: <Icons.IconClose width={20} height={20} color="#3E454D" />,
            onClick: () =>
              handleOpenModal(DraftPayrollModalTypeEnum.CLIENT_REJECTION),
          },
        ]
      : []),
    {
      title: data?.totalConfirmPayroll,
      description: 'Confirm Payroll',
      icon: <Icons.IconMoneyBillCheck />,
      onClick: () => handleOpenModal(DraftPayrollModalTypeEnum.CONFIRM_PAYROLL),
    },
  ];

  return (
    <div className="ClientApprovalReject h-full">
      <AppCard className="p-[24px] h-full" loading={loading}>
        <div className="flex justify-between items-center mb-[24px]">
          <AppIconCard
            iconName="IconFileText"
            title="Client Approval & Reject"
          />
        </div>
        <div className="grid grid-cols-2 gap-[16px] max-sm:grid-cols-1">
          {items.map((item) => (
            <div
              key={item.title}
              className="border border-[#CDDDD9] dashboard-hover-card rounded-[8px] p-[16px]"
              onClick={item.onClick}
            >
              <div
                className={`bg-warning-100 rounded-[8px] p-[8px] w-fit ${isBureauEmployer ? 'mb-[16px]' : 'mb-[34px]'}`}
              >
                {item.icon}
              </div>
              <div className="text-[22px] font-bold mb-[8px]">{item.title}</div>
              <div className="text-[14px] font-bold">{item.description}</div>
            </div>
          ))}
        </div>
      </AppCard>
      {modalType && (
        <DraftPayrollModal
          isOpen={isOpenModal}
          toggle={() => setIsOpenModal(false)}
          type={modalType}
          cbAfterAction={cbAfterAction}
        />
      )}
    </div>
  );
};

export default ClientApprovalReject;
